<script lang="ts" setup>import { computed as _computed } from 'vue';

import { onBeforeMount, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ISOPRO } from '~/models';
import { useMainStore } from '~/store';
import { Pagination } from '@pohlcon/design-system';

const { t } = useI18n();

interface Props {
  filteredProducts: ISOPRO[];
}

const props = defineProps<Props>();

const store = useMainStore();

const page = _computed({
  get (): number {
    calculatePageProducts();
    return store.paginationPage;
  },
  set (p: number) {
    store.paginationPage = p;
  }
});

const data = reactive({
  productsPerPage: 300,
  pageProducts: [] as ISOPRO[],
  vWidth: window.innerWidth
});

const emits = defineEmits<{
  (e: 'pageProductsChange', pageProducts: ISOPRO[]): void;
}>();

watch(() => props.filteredProducts, () => calculatePageProducts());
watch(() => data.pageProducts, () => emits('pageProductsChange', data.pageProducts as ISOPRO[]));

function calculatePageProducts (): void {
  if (!props.filteredProducts.length) {
    data.pageProducts = [];
  } else {
    const firstIndex = data.productsPerPage * (page.value - 1);
    const lastPosition = props.filteredProducts.length - 1;
    const lastIndex = Math.min(firstIndex + data.productsPerPage - 1, lastPosition);
    data.pageProducts = props.filteredProducts.slice(firstIndex, lastIndex + 1);
  }
}

onBeforeMount(() => {
  calculatePageProducts();
});
</script>

<template>
  <div id="products-pagination">
    <div>
      <b cypress-id="filtered-products-count">{{ filteredProducts.length }}</b>
      {{ t('selectView.productSelect.productsFound') }}
    </div>
    <Pagination v-model="page" :itemsPerPage="data.productsPerPage" :numberOfItems="filteredProducts.length" />
  </div>
  <slot :pageProducts="data.pageProducts"></slot>
</template>

<style lang="scss">
@use '../../../variables' as *;

#products-pagination {
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: center;
  margin-bottom: var(--gutter-m);

  .pagination__item {
    &:not(.spread)[disabled] {
      cursor: default;
    }
  }

  button {
    border: none;
  }

  @media screen and (max-width: $breakpoint-pagination) {
    .page-button {
      display: none;
    }
  }
}
</style>
